import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  History as HistoryIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Article as DocxIcon,
} from '@mui/icons-material';
import Markdown from 'markdown-to-jsx';
import { marked } from 'marked';
import html2pdf from 'html2pdf.js';
import {
  Document as DocxDocument,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
} from 'docx';
import { saveAs } from 'file-saver';
import type { Document, DocumentStatus } from '../../types/documents';

interface DocumentViewerProps {
  document: Document | null;
  onShowVersionHistory?: () => void;
  onCopy?: () => void;
  onDownload?: () => void;
}

const getStatusChip = (status: DocumentStatus) => {
  switch (status) {
    case 'DRAFT':
      return <Chip label="Draft" size="small" color="warning" />;
    case 'FINAL':
      return <Chip label="Final" size="small" color="success" />;
    case 'ARCHIVED':
      return <Chip label="Archived" size="small" color="default" />;
  }
};

/**
 * Convert markdown to HTML for clipboard operations
 */
const markdownToHtml = (markdown: string): string => {
  return marked.parse(markdown, { async: false }) as string;
};

/**
 * Parse markdown into structured content for DOCX generation
 */
const parseMarkdownForDocx = (markdown: string): Paragraph[] => {
  const paragraphs: Paragraph[] = [];
  const lines = markdown.split('\n');
  let listItems: string[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      listItems.forEach((item) => {
        paragraphs.push(
          new Paragraph({
            bullet: { level: 0 },
            children: parseInlineMarkdown(item),
            spacing: { after: 100 },
          })
        );
      });
      listItems = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();

    // Skip empty lines but flush lists
    if (!trimmedLine) {
      flushList();
      continue;
    }

    // Headers
    if (trimmedLine.startsWith('### ')) {
      flushList();
      paragraphs.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_3,
          children: [new TextRun({ text: trimmedLine.slice(4), bold: true })],
          spacing: { before: 200, after: 100 },
        })
      );
    } else if (trimmedLine.startsWith('## ')) {
      flushList();
      paragraphs.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_2,
          children: [new TextRun({ text: trimmedLine.slice(3), bold: true })],
          spacing: { before: 300, after: 150 },
        })
      );
    } else if (trimmedLine.startsWith('# ')) {
      flushList();
      paragraphs.push(
        new Paragraph({
          heading: HeadingLevel.HEADING_1,
          children: [new TextRun({ text: trimmedLine.slice(2), bold: true })],
          spacing: { before: 400, after: 200 },
        })
      );
    }
    // Horizontal rule
    else if (trimmedLine === '---' || trimmedLine === '***') {
      flushList();
      paragraphs.push(
        new Paragraph({
          border: { bottom: { style: 'single' as const, size: 1, color: 'CCCCCC' } },
          spacing: { before: 200, after: 200 },
        })
      );
    }
    // List items
    else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ') || /^\d+\.\s/.test(trimmedLine)) {
      const content = trimmedLine.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '');
      listItems.push(content);
    }
    // Regular paragraph
    else {
      flushList();
      paragraphs.push(
        new Paragraph({
          children: parseInlineMarkdown(trimmedLine),
          spacing: { after: 150 },
        })
      );
    }
  }

  // Flush any remaining list items
  flushList();

  return paragraphs;
};

/**
 * Parse inline markdown (bold, italic) into TextRuns
 */
const parseInlineMarkdown = (text: string): TextRun[] => {
  const runs: TextRun[] = [];
  // Simple regex to handle **bold** and *italic*
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|([^*]+))/g;
  let match;

  while ((match = regex.exec(text)) !== null) {
    if (match[2]) {
      // Bold text
      runs.push(new TextRun({ text: match[2], bold: true }));
    } else if (match[3]) {
      // Italic text
      runs.push(new TextRun({ text: match[3], italics: true }));
    } else if (match[4]) {
      // Regular text
      runs.push(new TextRun({ text: match[4] }));
    }
  }

  return runs.length > 0 ? runs : [new TextRun({ text })];
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  document,
  onShowVersionHistory,
  onCopy,
  onDownload,
}) => {
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleCopy = async () => {
    if (!document) return;

    try {
      // Convert markdown to HTML for rich text
      const htmlContent = markdownToHtml(document.content);

      // Create a styled HTML wrapper for better Word compatibility
      const styledHtml = `
        <html>
          <body style="font-family: Arial, sans-serif; font-size: 12pt; line-height: 1.6;">
            ${htmlContent}
          </body>
        </html>
      `;

      // Use the Clipboard API to write both HTML and plain text
      const blob = new Blob([styledHtml], { type: 'text/html' });
      const textBlob = new Blob([document.content], { type: 'text/plain' });

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': blob,
          'text/plain': textBlob,
        }),
      ]);

      setSnackbarMessage('Copied to clipboard with formatting');
      onCopy?.();
    } catch (err) {
      // Fallback to plain text if ClipboardItem is not supported
      console.warn('Rich copy failed, falling back to plain text:', err);
      try {
        await navigator.clipboard.writeText(document.content);
        setSnackbarMessage('Copied to clipboard (plain text)');
        onCopy?.();
      } catch (fallbackErr) {
        console.error('Failed to copy document:', fallbackErr);
        setSnackbarMessage('Failed to copy');
      }
    }
  };

  const handleDownloadClick = (event: React.MouseEvent<HTMLElement>) => {
    setDownloadMenuAnchor(event.currentTarget);
  };

  const handleDownloadMenuClose = () => {
    setDownloadMenuAnchor(null);
  };

  const handleDownloadPdf = async () => {
    handleDownloadMenuClose();
    if (!document) return;

    try {
      // Convert markdown to HTML
      const htmlContent = markdownToHtml(document.content);

      // Create a styled container for PDF generation
      const container = window.document.createElement('div');
      container.innerHTML = `
        <div style="font-family: Arial, sans-serif; font-size: 12pt; line-height: 1.6; padding: 20px;">
          <h1 style="color: #333; border-bottom: 2px solid #4caf50; padding-bottom: 10px; margin-bottom: 20px;">
            ${document.title}
          </h1>
          ${htmlContent}
        </div>
      `;

      const opt = {
        margin: [15, 15, 15, 15],
        filename: `${document.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const },
      };

      await html2pdf().set(opt).from(container).save();
      setSnackbarMessage('PDF downloaded');
      onDownload?.();
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      setSnackbarMessage('Failed to generate PDF');
    }
  };

  const handleDownloadDocx = async () => {
    handleDownloadMenuClose();
    if (!document) return;

    try {
      // Parse markdown into DOCX paragraphs
      const contentParagraphs = parseMarkdownForDocx(document.content);

      // Create DOCX document with title
      const doc = new DocxDocument({
        sections: [
          {
            properties: {},
            children: [
              // Title
              new Paragraph({
                heading: HeadingLevel.TITLE,
                alignment: AlignmentType.LEFT,
                children: [
                  new TextRun({
                    text: document.title,
                    bold: true,
                    size: 36, // 18pt
                  }),
                ],
                spacing: { after: 300 },
                border: {
                  bottom: { style: 'single' as const, size: 12, color: '4CAF50' },
                },
              }),
              // Metadata
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Version ${document.version} | Created: ${new Date(document.createdAt).toLocaleString()}`,
                    size: 20, // 10pt
                    color: '666666',
                  }),
                ],
                spacing: { after: 400 },
              }),
              // Content
              ...contentParagraphs,
            ],
          },
        ],
      });

      // Generate and download
      const buffer = await Packer.toBlob(doc);
      saveAs(buffer, `${document.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.docx`);
      setSnackbarMessage('Word document downloaded');
      onDownload?.();
    } catch (err) {
      console.error('Failed to generate DOCX:', err);
      setSnackbarMessage('Failed to generate Word document');
    }
  };

  if (!document) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          p: 4,
        }}
      >
        <DescriptionIcon sx={{ fontSize: 64, mb: 2 }} />
        <Typography variant="h6">Select a document to view</Typography>
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
          Choose a document from the list on the left to view its contents.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Document Header */}
      <Paper elevation={1} sx={{ p: 2, mb: 2, flexShrink: 0 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" component="h2">
                {document.title}
              </Typography>
              {getStatusChip(document.status)}
            </Box>
            {document.templateName && (
              <Typography variant="body2" color="text.secondary">
                Template: {document.templateName}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Copy with formatting">
              <IconButton onClick={handleCopy} size="small">
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download">
              <IconButton onClick={handleDownloadClick} size="small">
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {onShowVersionHistory && (
              <Tooltip title="Version history">
                <IconButton onClick={onShowVersionHistory} size="small">
                  <HistoryIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label={`Version ${document.version}`} size="small" variant="outlined" />
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Created: {new Date(document.createdAt).toLocaleString()}
          </Typography>
          {document.updatedAt !== document.createdAt && (
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Updated: {new Date(document.updatedAt).toLocaleString()}
            </Typography>
          )}
        </Box>

        {document.revisionPrompt && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              <strong>Revision:</strong> {document.revisionPrompt}
            </Typography>
          </Alert>
        )}
      </Paper>

      {/* Context Snapshot */}
      {document.contextSnapshot && Object.keys(document.contextSnapshot).length > 0 && (
        <Paper elevation={1} sx={{ p: 2, mb: 2, flexShrink: 0 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Clinical Context
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {document.contextSnapshot.patientName && (
              <Chip
                label={`Patient: ${document.contextSnapshot.patientName}`}
                size="small"
                variant="outlined"
              />
            )}
            {document.contextSnapshot.species && (
              <Chip
                label={document.contextSnapshot.species}
                size="small"
                variant="outlined"
              />
            )}
            {document.contextSnapshot.breed && (
              <Chip
                label={document.contextSnapshot.breed}
                size="small"
                variant="outlined"
              />
            )}
            {document.contextSnapshot.patientAge && (
              <Chip
                label={document.contextSnapshot.patientAge}
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          {document.contextSnapshot.diagnoses && document.contextSnapshot.diagnoses.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Diagnoses: {document.contextSnapshot.diagnoses.join(', ')}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Document Content */}
      <Paper
        elevation={1}
        sx={{
          flex: 1,
          p: 3,
          overflow: 'auto',
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            '& h1': { fontSize: '1.75rem', mb: 2 },
            '& h2': { fontSize: '1.5rem', mt: 3, mb: 2 },
            '& h3': { fontSize: '1.25rem', mt: 2, mb: 1 },
            '& p': { mb: 1.5, lineHeight: 1.7 },
            '& ul, & ol': { pl: 3, mb: 2 },
            '& li': { mb: 0.5 },
            '& strong': { fontWeight: 600 },
            '& hr': { my: 3, border: 'none', borderTop: '1px solid', borderColor: 'divider' },
          }}
        >
          <Markdown>{document.content}</Markdown>
        </Box>
      </Paper>

      {/* Download Menu */}
      <Menu
        anchorEl={downloadMenuAnchor}
        open={Boolean(downloadMenuAnchor)}
        onClose={handleDownloadMenuClose}
      >
        <MenuItem onClick={handleDownloadPdf}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download as PDF</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDownloadDocx}>
          <ListItemIcon>
            <DocxIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download as Word (.docx)</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar for feedback */}
      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={3000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default DocumentViewer;
